import type { ArticleSummary, TextbookCatalogNode } from '@en3/shared';
import { Badge } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import { ArticleCard, ArticleEmptyHint } from './ArticleCard';

/**
 * 教材目录树：`教材 → 单元 → 文章`，外加一节「未归入单元的文章」。
 *
 * 这是**纯展示组件**：所有数据与展开状态都从 props 进来。
 * 为什么不在这里自己发请求：目录页的标题搜索是本地过滤，过滤后的树 = 原始树 + 过滤函数，
 * 如果让树组件自己取数，搜索一变就要重新请求，而用户要的是"输入即过滤"。
 *
 * 两个刻意的设计：
 * 1. **展开状态由外部持有**（`expandedIds` / `onToggle`），因此可以持久化到 localStorage，
 *    用户刷新后仍停在昨天展开的那一本。
 * 2. **单元标题与教材标题都做中英并列**，且缺中文时不显示副标题，
 *    而不是显示"—"：占位符会让整棵树看起来很脏。
 */

export interface TextbookTreeProps {
  nodes: TextbookCatalogNode[];
  expandedIds: ReadonlySet<string>;
  onToggle: (textbookId: string) => void;
  onOpenArticle: (article: ArticleSummary) => void;
  /** 本地过滤把某一本教材下的内容全部滤掉时，为 true —— 整本隐藏，避免只剩空壳 */
  isTextbookVisible?: (node: TextbookCatalogNode) => boolean;
  className?: string;
}

export function TextbookTree({
  nodes,
  expandedIds,
  onToggle,
  onOpenArticle,
  isTextbookVisible,
  className,
}: TextbookTreeProps) {
  const visibleNodes = isTextbookVisible ? nodes.filter(isTextbookVisible) : nodes;

  return (
    <div className={cn('space-y-3', className)}>
      {visibleNodes.map((node) => (
        <TextbookSection
          key={node.id}
          node={node}
          expanded={expandedIds.has(node.id)}
          onToggle={() => onToggle(node.id)}
          onOpenArticle={onOpenArticle}
        />
      ))}
    </div>
  );
}

function TextbookSection({
  node,
  expanded,
  onToggle,
  onOpenArticle,
}: {
  node: TextbookCatalogNode;
  expanded: boolean;
  onToggle: () => void;
  onOpenArticle: (article: ArticleSummary) => void;
}) {
  const meta = [node.publisher, node.edition].filter((item): item is string => Boolean(item)).join(' · ');

  return (
    <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-accent/40"
        >
          <Chevron open={expanded} />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold break-words">{node.title}</span>
            {node.titleZh && (
              <span className="mt-0.5 block text-xs break-words text-muted-foreground">{node.titleZh}</span>
            )}
            <span className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge tone="muted">{node.level}</Badge>
              <Badge tone="muted">
                {node.unitCount} 单元 / {node.articleCount} 篇正文
              </Badge>
              {node.copyrightStatus === 'public_metadata' && (
                <Badge tone="warning" title="平台只收录公开的目录元数据，课文正文需由有权限的用户导入">
                  仅公开目录
                </Badge>
              )}
              {node.visibility === 'private' && <Badge tone="muted">私有教材</Badge>}
            </span>
            {meta && <span className="mt-1 block text-xs text-muted-foreground">{meta}</span>}
          </span>
        </button>
      </h3>

      {expanded && (
        <div className="space-y-4 border-t px-3 py-3">
          {node.description && <p className="text-xs text-muted-foreground">{node.description}</p>}

          {node.units.length === 0 && node.looseArticles.length === 0 && (
            <ArticleEmptyHint text="这本教材还没有单元，也没有正文。教师或管理员可以新建单元，再往单元里导入正文。" />
          )}

          {node.units.map((unit) => (
            <div key={unit.id}>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h4 className="text-sm font-medium">
                  Unit {unit.unitNo}
                  <span className="ml-1.5">{unit.title}</span>
                </h4>
                {unit.titleZh && <span className="text-xs text-muted-foreground">{unit.titleZh}</span>}
                <Badge tone="muted">{unit.articleCount} 篇</Badge>
              </div>
              {unit.summary && <p className="mt-1 text-xs text-muted-foreground">{unit.summary}</p>}

              <div className="mt-2 space-y-2">
                {unit.articles.length === 0 ? (
                  <ArticleEmptyHint text="这个单元还没有正文。可以把已有文章归到该单元，或直接导入新正文。" />
                ) : (
                  unit.articles.map((article) => (
                    <ArticleCard key={article.id} article={article} onOpen={onOpenArticle} />
                  ))
                )}
              </div>
            </div>
          ))}

          {node.looseArticles.length > 0 && (
            <div>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <h4 className="text-sm font-medium">未归入单元的文章</h4>
                <Badge tone="muted">{node.looseArticles.length} 篇</Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                导入时没有选择单元的文章会落在这里；后续可在“全部文章”里编辑归属。
              </p>
              <div className="mt-2 space-y-2">
                {node.looseArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} onOpen={onOpenArticle} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** 折叠指示器：用旋转的 chevron 而不是"展开/收起"文字，省一行宽度（375px 下很关键）。 */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/**
 * 目录页标题搜索的本地过滤（**不请求后端**）。
 *
 * 为什么本地过滤而不是 `?q=`：目录树已经在一次请求里全部拿到（`withUnits: true`），
 * 只有几十到几百个节点，本地匹配既没有网络延迟也不会打断输入。
 * 后端搜索留给「全部文章」视图（那里分页，本地过滤看不到第二页的数据）。
 *
 * 无关键词时返回原数组（引用不变），这样 `useMemo` 的依赖稳定，
 * 也不会因为每次过滤生成新数组而让下游反复重渲染。
 */
export function filterCatalogNodes(nodes: TextbookCatalogNode[], keyword: string): TextbookCatalogNode[] {
  const needle = keyword.trim().toLowerCase();
  if (needle.length === 0) return nodes;

  const text = (...values: (string | null)[]) =>
    values.some((value) => value !== null && value.toLowerCase().includes(needle));

  const matchArticle = (article: ArticleSummary) => text(article.title, article.titleZh);

  // 命中教材自身的元数据时，整本保留（用户搜"新视野"就是想看这一本的全部内容）
  const textbookSelfHit = (node: TextbookCatalogNode) =>
    text(node.title, node.titleZh, node.publisher, node.edition, node.level);

  const result: TextbookCatalogNode[] = [];

  for (const node of nodes) {
    const selfHit = textbookSelfHit(node);
    const units: TextbookCatalogNode['units'] = [];
    let looseArticles = node.looseArticles;

    for (const unit of node.units) {
      if (selfHit || text(unit.title, unit.titleZh, unit.theme, unit.summary)) {
        units.push(unit);
        continue;
      }
      // 单元标题没命中：再看单元下的文章。命中就把单元连同命中文章一起保留，
      // 不命中就整个丢掉 —— 留下一个空单元会让搜索结果看起来像是没过滤干净。
      const articles = unit.articles.filter(matchArticle);
      if (articles.length > 0) units.push({ ...unit, articles });
    }

    if (!selfHit) looseArticles = looseArticles.filter(matchArticle);

    // 教材自身没命中、单元与松散文章也都没命中时整本丢掉
    if (selfHit || units.length > 0 || looseArticles.length > 0) {
      result.push({ ...node, units, looseArticles });
    }
  }

  return result;
}
